import React, { useEffect } from 'react';
import { swipeIsFree } from './utils/swipeOwner';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SubCalendarProvider } from './context/SubCalendarContext';
import LandingPage from './components/LandingPage';
import TodayView from './components/TodayView';
import EventForm from './components/EventForm';
import SettingsPage from './components/SettingsPage';
import TasksPage from './components/TasksPage';
import SearchPage from './components/SearchPage';
import LabelAgenda from './components/LabelAgenda';
import SpeedReport from './components/SpeedReport'; // TEMPORARY — see perfLog.js
import GroupsPage from './components/GroupsPage';
import WelcomeScreen from './components/WelcomeScreen';
import OnboardingSlides from './components/OnboardingSlides';
import ConnectCalendar from './components/ConnectCalendar';
import UpdateBanner from './components/UpdateBanner';
import AlarmManager from './components/AlarmManager';
import { useT } from './i18n';
import { requestLeave } from './utils/unsavedGuard';
import ErrorBoundary from './components/ErrorBoundary';
import { usePushSubscription } from './hooks/usePushSubscription';
import { useNativeAlarms } from './hooks/useNativeAlarms';
import './styles/global.css';
import './styles/components.css';

function AuthCallback() {
  // OAuth now uses httpOnly cookies — just redirect home
  return <Navigate to="/" replace />;
}

// A new user is someone who has never completed onboarding.
// Primary check: localStorage (fast, works across sessions on same device).
// Fallbacks (for users on a new device with no localStorage):
//   1. account older than 10 minutes → clearly not a first-time setup
//   2. telegram_chat_id is set → went through onboarding before
// (No language check: accounts get a browser-detected language like 'it' at
//  signup, which used to make every non-English new user skip the welcome.)
function isNewUser(user) {
  if (!user?.id) return false;
  if (localStorage.getItem('onboarded_' + user.id)) return false;
  // How OLD the account is only matters when the flag above is missing — a new
  // phone, cleared storage. It is a guard against showing the welcome to
  // somebody who has been using MeMo for months, nothing more.
  //
  // It used to be ten minutes, which quietly did something else entirely: sign
  // up, get interrupted by a phone call, come back twenty minutes later, and
  // the welcome was gone for good. You landed on an empty day with nothing to
  // say what this was or what to do — the one moment where a person decides
  // whether to keep an app, spent staring at nothing.
  //
  // A week instead. The worst it can now cost is somebody seeing the welcome
  // twice in their first days on a second device, which is a small price
  // against never seeing it at all.
  const WELCOME_GRACE_MS = 7 * 24 * 60 * 60 * 1000;
  if (user.created_at) {
    const ageMs = Date.now() - new Date(user.created_at).getTime();
    if (ageMs > WELCOME_GRACE_MS) return false;
  }
  if (user.telegram_chat_id) return false;
  return true;
}

function detectBrowserLang() {
  const supported = ['it', 'en', 'es', 'de', 'fr', 'nl', 'pt'];
  const l = navigator.language?.slice(0, 2).toLowerCase();
  return supported.includes(l) ? l : 'it';
}

function AuthError() {
  const T = useT(detectBrowserLang());
  return (
    <div className="login-page">
      <div className="login-logo">⚠️</div>
      <h1 className="login-title">{T.authError.title}</h1>
      <p className="login-subtitle">{T.authError.subtitle}</p>
      <a href="/" className="google-btn" style={{ justifyContent: 'center' }}>{T.authError.back}</a>
    </div>
  );
}

function BottomNav() {
  const { user } = useAuth();
  const T = useT(user?.language);
  const navigate = useNavigate();

  // The bar sits under your thumb on every screen, including the event form.
  // Brushing it while writing an appointment used to discard the lot without a
  // word. Now the screen holding unsaved work gets to ask first.
  const guardedNav = (e) => {
    // The tap usually lands on the icon or the label inside the link.
    const a = e.target?.closest?.('a[href]');
    if (!a) return;
    const to = a.getAttribute('href') || '/';
    if (!requestLeave(() => navigate(to))) e.preventDefault();
  };

  return (
    <nav className="bottom-nav" onClickCapture={guardedNav}>
      <NavLink to="/" end>
        <span className="nav-icon">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
        </span>
        <span>{T.nav.today}</span>
      </NavLink>
      <NavLink to="/search">
        <span className="nav-icon">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.5" y2="16.5"/>
          </svg>
        </span>
        <span>{T.nav.search}</span>
      </NavLink>
      <NavLink to="/tasks">
        <span className="nav-icon">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/>
          </svg>
        </span>
        <span>{T.nav.tasks}</span>
      </NavLink>
      <NavLink to="/settings">
        <span className="nav-icon">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
          </svg>
        </span>
        <span>{T.nav.settings}</span>
      </NavLink>
    </nav>
  );
}

// Swipe-to-navigate between the bottom-bar sections: withdrawn on purpose.
//
// It used to be ['/', '/search', '/tasks', '/settings']. Then the calendar
// screens took the sideways drag to mean "another day, another week, another
// month" — which is what every calendar on both phones does — and the gesture
// ended up meaning two different things depending on where you were standing.
// Silvia: "credo sia meglio toglierlo per dare uniformità all'uso delle
// sezioni", and she is right. One gesture, one meaning: sideways moves you
// through time. The sections are one tap away in the bar at the bottom, which
// is where they have always been and where they are visible.
//
// Everything below still works; putting routes back in this list is all it
// would take to bring the old behaviour back.
const SWIPE_ROUTES = [];

function AppShell() {
  const { user, loading, unreachable, retryAuth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const T = useT(user?.language);
  const [localTheme, setLocalTheme] = React.useState(() => user?.theme || 'light');

  // Ref to the <main> element — translateX during drag runs on the GPU compositor
  const mainRef = React.useRef(null);

  // ── Drag-to-navigate — native-feel swipe ─────────────────────────────────
  // Three mechanics make this feel like a real app:
  //   1. 1:1 finger tracking  — the page follows the finger with no damping
  //   2. Velocity flick       — a fast short swipe navigates immediately
  //   3. Directional entry    — new page slides in from the correct side
  //      (left swipe → new page enters from the right, and vice-versa)
  useEffect(() => {
    // Nothing claims the gesture any more (see SWIPE_ROUTES): do not even
    // attach the listeners. A non-passive touchmove handler costs scrolling
    // smoothness on every page it is attached to, and it would be paying that
    // for a behaviour that can no longer happen.
    if (SWIPE_ROUTES.length === 0) return undefined;
    let startX = 0, startY = 0;
    let lastX = 0, lastT = 0, vx = 0;           // velocity tracking
    let fired = false, active = false, axisLocked = false;

    function m() { return mainRef.current; }

    // Set or clear a translateX on <main>; transition is the full CSS value
    function setT(x, transition = 'none') {
      const el = m(); if (!el) return;
      el.style.transition = transition;
      el.style.transform  = x === 0 ? '' : `translateX(${x}px)`;
    }

    function onStart(e) {
      const tag = e.target?.tagName ?? '';
      // Never activate swipe on form controls, buttons, or links.
      // Without this, touchmove.preventDefault() (called when horizontal axis
      // locks) kills the click event on those elements on iOS/Android.
      if (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(tag)) { active = false; return; }
      if (e.target?.closest?.('button, a')) { active = false; return; }
      // Engage ONLY on the main swipeable tabs. On other pages (the event form,
      // /edit, etc.) staying out keeps vertical scrolling fully native — the
      // non-passive touchmove handler otherwise makes iOS Safari scroll janky
      // and can "freeze" while filling in a long event form.
      if (SWIPE_ROUTES.indexOf(window.location.pathname) < 0) { active = false; return; }
      // The day view takes the sideways drag for itself: on a calendar a
      // sideways drag means "another day", which is what Jodi asked for and
      // what every calendar on both phones does. Sections are still one tap
      // away in the bar at the bottom. Week, month and year do not claim it,
      // so there this still moves between sections. See utils/swipeOwner.js.
      if (!swipeIsFree()) { active = false; return; }
      startX = lastX = e.touches[0].clientX;
      startY         = e.touches[0].clientY;
      lastT = e.timeStamp; vx = 0;
      fired = false; active = true; axisLocked = false;
      // Promote to GPU compositor layer only while dragging so that
      // position:fixed children (e.g. EventForm action bar) work correctly at rest.
      if (m()) m().style.willChange = 'transform';
      // Cancel any in-progress entry animation so dragging feels instant
      m()?.classList.remove('page-enter');
      setT(0);
    }

    function onMove(e) {
      if (!active || fired) return;

      const cx = e.touches[0].clientX;
      const cy = e.touches[0].clientY;
      const dx = cx - startX;
      const dy = cy - startY;
      const dt = e.timeStamp - lastT;
      if (dt > 0) vx = (cx - lastX) / dt;  // px/ms (+ve = rightward)
      lastX = cx; lastT = e.timeStamp;

      const adx = Math.abs(dx), ady = Math.abs(dy);
      if (adx < 5 && ady < 5) return;
      if (!axisLocked) {
        if (ady >= adx) {
          // Vertical gesture — let the browser scroll. Clear the will-change we
          // optimistically set in onStart: leaving it on <main> establishes a
          // containing block for position:fixed children (the Notes/Search
          // bottom bar), which then drifts and covers content while scrolling.
          active = false;
          if (m()) m().style.willChange = '';
          return;
        }
        axisLocked = true;
      }

      e.preventDefault(); // block iOS rubber-band / back gesture

      const path     = window.location.pathname;
      const swipable = !path.startsWith('/edit/');
      const idx      = swipable ? SWIPE_ROUTES.indexOf(path) : -1;
      const canNext  = dx < 0 && idx >= 0 && idx < SWIPE_ROUTES.length - 1;
      const canPrev  = dx > 0 && idx > 0;

      // 1:1 when navigation is possible; heavy rubber-band at dead ends
      setT(dx * (canNext || canPrev ? 1 : 0.08));

      // Navigate on distance threshold (28 px) OR velocity flick (>350 px/s)
      const isFlick = Math.abs(vx) > 0.35;
      if ((!canNext && !canPrev) || (adx < 28 && !isFlick)) return;

      fired = true;
      const forward = canNext; // left swipe = going forward in SWIPE_ROUTES
      navigate(forward ? SWIPE_ROUTES[idx + 1] : SWIPE_ROUTES[idx - 1]);

      // After React commits the new page: snap to 0, then animate in from the
      // correct direction (forward = new content enters from the right, and
      // vice-versa). Two rAFs ensure React has painted before we start.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const el = m(); if (!el) return;
        el.style.transition  = 'none';
        el.style.transform   = '';
        el.dataset.dir = forward ? 'fwd' : 'back';
        el.getBoundingClientRect(); // flush styles before adding animation class
        el.classList.add('page-enter');
        setTimeout(() => {
          el?.classList.remove('page-enter');
          if (el) el.style.willChange = ''; // demote after entry animation
        }, 350);
      }));
    }

    function onEnd() {
      // If the user lifted without crossing the threshold, spring back
      if (active && !fired) {
        setT(0, 'transform 0.28s cubic-bezier(0.17,0.84,0.44,1)');
        // Demote GPU layer after spring-back so position:fixed works again
        setTimeout(() => { if (m()) m().style.willChange = ''; }, 300);
      } else if (m() && m().style.willChange) {
        // Safety net: never leave will-change on <main>, or position:fixed
        // children stop being viewport-anchored and drift while scrolling.
        m().style.willChange = '';
      }
      fired = false; active = false; axisLocked = false; vx = 0;
    }

    document.addEventListener('touchstart',  onStart, { passive: true  });
    document.addEventListener('touchmove',   onMove,  { passive: false });
    document.addEventListener('touchend',    onEnd,   { passive: true  });
    document.addEventListener('touchcancel', onEnd,   { passive: true  });

    return () => {
      document.removeEventListener('touchstart',  onStart);
      document.removeEventListener('touchmove',   onMove);
      document.removeEventListener('touchend',    onEnd);
      document.removeEventListener('touchcancel', onEnd);
    };
  }, [navigate]);

  // Subscribe to push notifications once the user is logged in
  usePushSubscription(!!user);
  // …and, inside the installed Android app (where Web Push doesn't exist), hand
  // the upcoming reminders to Android itself so they fire with MeMo closed.
  useNativeAlarms(user);

  useEffect(() => {
    const theme = user?.theme || 'light';
    setLocalTheme(theme);
    document.documentElement.className = theme === 'dark' ? 'theme-dark' : '';
  }, [user?.theme]);

  // The page's declared language, and right-to-left for Arabic.
  //
  // The lang half is not a detail — it caused the strangest bug of the whole
  // test. index.html shipped a fixed `lang="it"`, and nothing ever changed it.
  // So a tester in the USA, reading an English interface, had a browser
  // convinced the page was Italian. It offered to translate, she accepted, and
  // Chrome rewrote what was on screen:
  //
  //   • the logo is built as M + e + M + a clock. The Italian word "e" means
  //     "and", so the logo read "MAndM".
  //   • her 08:00 entry, "Ride", is a real Italian verb — third person of
  //     "ridere". It came out as "Laughs".
  //
  // She was looking at her own calendar with her own events renamed, and MeMo
  // looked broken in a way no log would ever show. Opening the event showed the
  // right title, because browsers leave form fields alone — which is exactly the
  // clue that gave it away.
  //
  // Declaring the language correctly stops the browser offering in the first
  // place; translate="no" on user content (event titles, notes, labels) is the
  // second line, for anyone who translates deliberately. Nobody's appointments
  // should be run through a dictionary.
  useEffect(() => {
    const lang = user?.language || navigator.language || 'en';
    const isRTL = lang === 'ar' || lang.startsWith('ar-');
    document.documentElement.lang = lang;
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    // Remembered for the NEXT load, where it is read before the first pixel
    // (see the script at the top of index.html). Without this the first frame
    // has to guess from the browser's language, which is usually right but not
    // for somebody reading MeMo in a language other than their phone's.
    try { localStorage.setItem('memo_lang', lang); } catch (_) {}
  }, [user?.language]);

  // "Connect calendar" intent from the landing (?connect=calendar). Persist it
  // so it survives the OAuth round-trip, then open the connect screen once the
  // user is logged in.
  useEffect(() => {
    try {
      if (new URLSearchParams(window.location.search).get('connect') === 'calendar') {
        localStorage.setItem('memo_connect', '1');
      }
    } catch (_) {}
  }, []);
  useEffect(() => {
    if (!user) return;
    let pending = false;
    try { pending = localStorage.getItem('memo_connect') === '1'; } catch (_) {}
    if (pending) {
      try { localStorage.removeItem('memo_connect'); } catch (_) {}
      navigate('/connect');
    }
  }, [user, navigate]);

  function toggleTheme() {
    const next = localTheme === 'dark' ? 'light' : 'dark';
    setLocalTheme(next);
    document.documentElement.className = next === 'dark' ? 'theme-dark' : '';
    // Persist to backend silently
    import('./api/client').then(({ default: api }) => {
      api.put('/settings', { theme: next }).catch(() => {
      // Saved locally either way; only the cross-device preference is lost.
    });
    });
  }

  if (loading) {
    return (
      <div className="app-shell">
        <div className="page-content"><div className="spinner" /></div>
      </div>
    );
  }

  // Auth callback and error pages don't need the shell
  if (location.pathname === '/auth/callback') return <AuthCallback />;
  if (location.pathname === '/auth/error') return <AuthError />;

  // Could not reach the server at all. This is NOT "you are logged out", and
  // showing the landing page for it was the whole of "MeMo signs me out every
  // so often": a tunnel on the way to work, and you are asked to sign in again
  // although the session was never in question.
  if (!user && unreachable) {
    const TXT = {
      it: { t: 'Non riesco a raggiungere MeMo', s: 'Controlla la connessione. I tuoi dati sono al sicuro: non serve rientrare.', b: 'Riprova' },
      en: { t: "Can't reach MeMo", s: 'Check your connection. Your data is safe — there is no need to sign in again.', b: 'Retry' },
      es: { t: 'No puedo conectar con MeMo', s: 'Comprueba la conexión. Tus datos están a salvo: no hace falta volver a entrar.', b: 'Reintentar' },
      de: { t: 'MeMo ist nicht erreichbar', s: 'Prüfe die Verbindung. Deine Daten sind sicher — du musst dich nicht neu anmelden.', b: 'Erneut versuchen' },
      fr: { t: 'Impossible de joindre MeMo', s: 'Vérifiez la connexion. Vos données sont en sécurité, inutile de vous reconnecter.', b: 'Réessayer' },
      ru: { t: "Не удаётся связаться с MeMo", s: "Проверьте соединение. Ваши данные в безопасности — входить заново не нужно.", b: "Повторить" },
      pl: { t: "Nie mogę połączyć się z MeMo", s: "Sprawdź połączenie. Twoje dane są bezpieczne — nie musisz logować się ponownie.", b: "Spróbuj ponownie" },
      ro: { t: "Nu reușesc să contactez MeMo", s: "Verifică conexiunea. Datele tale sunt în siguranță — nu trebuie să intri din nou.", b: "Reîncearcă" },
      zh: { t: "无法连接 MeMo", s: "请检查网络连接。你的数据是安全的，无需重新登录。", b: "重试" },
      ja: { t: "MeMo に接続できません", s: "通信状態をご確認ください。データは安全です。ログインし直す必要はありません。", b: "再試行" },
      hi: { t: "MeMo से संपर्क नहीं हो पा रहा", s: "अपना कनेक्शन जाँचें। आपका डेटा सुरक्षित है — दोबारा साइन इन करने की ज़रूरत नहीं।", b: "फिर कोशिश करें" },
      ko: { t: "MeMo에 연결할 수 없습니다", s: "연결 상태를 확인하세요. 데이터는 안전합니다 — 다시 로그인할 필요는 없습니다.", b: "다시 시도" },
      ar: { t: "تعذّر الوصول إلى MeMo", s: "تحقّق من الاتصال. بياناتك آمنة — لا حاجة لتسجيل الدخول من جديد.", b: "إعادة المحاولة" },
      nl: { t: 'MeMo is niet bereikbaar', s: 'Controleer je verbinding. Je gegevens zijn veilig — opnieuw inloggen hoeft niet.', b: 'Opnieuw' },
      pt: { t: 'Não consigo aceder ao MeMo', s: 'Verifica a ligação. Os teus dados estão seguros — não precisas de entrar de novo.', b: 'Tentar de novo' },
      'pt-BR': { t: 'Não consigo acessar o MeMo', s: 'Verifique a conexão. Seus dados estão seguros — não precisa entrar de novo.', b: 'Tentar de novo' },
    };
    // The user's exact locale first, so a Brazilian is not handed European
    // Portuguese; then the two-letter fallback; then English.
    const L = TXT[navigator.language] || TXT[detectBrowserLang()] || TXT.en;
    return (
      <div className="app-shell">
        <div className="page-content" style={{ textAlign: 'center', paddingTop: 80 }}>
          <p style={{ fontWeight: 800, fontSize: '1.05rem', marginBottom: 8 }}>{L.t}</p>
          <p style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)', marginBottom: 20, lineHeight: 1.5 }}>{L.s}</p>
          <button type="button" className="btn btn-outline" onClick={retryAuth}>{L.b}</button>
        </div>
      </div>
    );
  }

  if (!user) return <LandingPage />;

  // Show welcome screen for first-time users
  if (isNewUser(user)) return <WelcomeScreen />;

  // Right after the welcome setup, show the guided intro slides once.
  // (?tour=1 forces a preview for anyone — handy to review the tour.)
  if (localStorage.getItem('tour_pending_' + user.id) ||
      window.location.search.includes('tour=1')) return <OnboardingSlides />;

  // Focused "connect your calendar" screen (from landing CTA or the Calendari button)
  if (location.pathname === '/connect') return <ConnectCalendar />;

  return (
    <div className="app-shell">
      <header className="top-bar">
        <h1>
          <span className="memo-logo" translate="no">
            M<span className="memo-logo-e">e</span>M<svg className="memo-logo-clock" viewBox="0 0 24 24" width="1.05em" height="1.05em" aria-hidden="true">
              <circle cx="12" cy="12" r="10.2" style={{ stroke: 'var(--logo-from, #1a4a35)' }} strokeWidth="2.8" fill="none"/>
              <line x1="12" y1="12" x2="12" y2="5" style={{ stroke: 'var(--logo-from, #1a4a35)' }} strokeWidth="2.4" strokeLinecap="round"/>
              <line x1="12" y1="12" x2="17" y2="15" style={{ stroke: 'var(--logo-to, #52b788)' }} strokeWidth="2.2" strokeLinecap="round"/>
            </svg>
          </span>
        </h1>
        <div className="top-bar-actions">
          {user.picture && (
            <img src={user.picture} alt="avatar" className="avatar" referrerPolicy="no-referrer" />
          )}
        </div>
      </header>

      {/* will-change: transform promotes <main> to a GPU compositor layer so
          the drag-to-navigate translateX runs at 60 fps with no jank        */}
      <main ref={mainRef} className="page-content">
        <Routes>
          <Route path="/" element={<TodayView />} />
          <Route path="/new" element={<EventForm />} />
          <Route path="/edit/:id" element={<EventForm />} />
          <Route path="/label/:id" element={<LabelAgenda />} />
          {/* The same screen, asked about a shared calendar instead of a label. */}
          <Route path="/shared/:id" element={<LabelAgenda shared />} />
          {/* The same screen again, asked about an icon: "when are the
              birthdays?". Three filters, one page. */}
          <Route path="/icon/:id" element={<LabelAgenda icon />} />
          {/* TEMPORARY measuring instrument — nothing links here, you reach it
              by typing /velocita. Remove before launch (see utils/perfLog.js). */}
          <Route path="/velocita" element={<SpeedReport />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          {/* Old bookmark/PWA-cached path */}
          <Route path="/notes" element={<Navigate to="/tasks" replace />} />
          <Route path="/groups" element={<GroupsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {/* Quick-add FAB — ONLY on the Today page (Silvia's decision, 04.07.2026:
          it doesn't belong on Search/Tasks/anywhere else — whitelist, so it can
          never reappear on other pages). */}
      {location.pathname === '/' && (
        <NavLink to="/new" className="fab" title={T.nav.add}>＋</NavLink>
      )}

      <BottomNav />
      <UpdateBanner T={T} />
      {/* App-wide reminder alarm — rings on any screen, not just Today */}
      {user && <AlarmManager />}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SubCalendarProvider>
          {/* A screen that throws while drawing used to take the whole app with
              it — the blank page with no way out. Now it becomes a sentence, a
              way back, and a report that arrives without anybody writing in. */}
          <ErrorBoundary>
            <AppShell />
          </ErrorBoundary>
        </SubCalendarProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
